/**
 * 虚拟键盘 UI 键码 ↔ 智慧无限矩阵 3 字节单元（TYPE / CODE_L / CODE_H）
 */

import {
  isZhwxFnSyntheticKeyCode,
  ZHWX_CON_TYPE,
  // ZHWX_FN_SYNTHETIC_BASE,
  ZHWX_FN_UI_KEYCODE,
  ZHWX_FUNC_TYPE,
  ZHWX_UI_TO_CONSUMER_CELL,
} from "./zhwxFnDisplay";
import {
  readZhwxMacroSlotPref,
  ZHWX_MACRO_SLOT_COUNT,
  ZHWX_MACRO_UI_BASE,
  type ZhwxMacroSlotPref,
} from "./zhwxMacro";

export const ZHWX_KB_TYPE = 0x10;

export type ZhwxMatrixCellWrite = {
  type: number;
  codeL: number;
  codeH: number;
};

/** UI HID 修饰键 224~231 → 协议热键位掩码 */
const UI_HID_TO_ZHWX_HOTKEY: Readonly<Record<number, number>> = {
  224: 0x01,
  225: 0x02,
  226: 0x04,
  227: 0x08,
  228: 0x10,
  229: 0x20,
  230: 0x40,
  231: 0x80,
};

/** 改键面板图标码 → 智慧无限 FUNC codeL */
const ZHWX_UI_ICON_TO_FUNC: Readonly<Record<number, number>> = {
  61952: 0x05,
  61961: 0x04,
  62208: 0x2c,
  62209: 0x29,
  62210: 0x21,
  62211: 0x22,
  62212: 0x24,
  62213: 0x25,
  62215: 0x2b,
};

/** 宏按键：播放一次（停止方式见 codeH） */
export const ZHWX_MACRO_1_TYPE = 0x50;
/** 宏按键：指定播放次数 */
export const ZHWX_MACRO_N_TYPE = 0x51;
/** 宏按键：枪宏 */
export const ZHWX_MACRO_GUN_TYPE = 0x52;

/**
 * 按宏槽偏好生成矩阵 3 字节（TYPE=0x50/0x51，codeL=槽号，codeH=停止方式或次数）。
 * slot 为 0 基 UI 槽号：M0→0（绑键 `50 00 xx`），M1→1；与 macro_tab 地址表索引一致。
 * 与抓包矩阵区 `50 10 00`（槽16）形式一致；普通方向键出厂为 `10 00 50`（KB_TYPE+KEY_LEFT）。
 */
export function buildZhwxMacroMatrixCell(
  slot: number,
  pref?: Pick<ZhwxMacroSlotPref, "bindMode" | "stopMethod" | "playCount">
): ZhwxMatrixCellWrite {
  const bindPref = pref ?? readZhwxMacroSlotPref(slot);
  const safeSlot =
    Math.max(0, Math.min(ZHWX_MACRO_SLOT_COUNT - 1, slot)) & 0xff;
  if (bindPref.bindMode === "count") {
    return {
      type: ZHWX_MACRO_N_TYPE,
      codeL: safeSlot,
      codeH: Math.max(1, Math.min(255, Math.round(bindPref.playCount))) & 0xff,
    };
  }
  return {
    type: ZHWX_MACRO_1_TYPE,
    codeL: safeSlot,
    codeH: Math.max(0, Math.min(3, Math.round(bindPref.stopMethod))) & 0xff,
  };
}

function writeFuncCell(codeL: number): ZhwxMatrixCellWrite {
  return { type: ZHWX_FUNC_TYPE, codeL: codeL & 0xff, codeH: 0 };
}

function writeKbCell(codeL: number, codeH: number): ZhwxMatrixCellWrite {
  return {
    type: ZHWX_KB_TYPE,
    codeL: codeL & 0xff,
    codeH: codeH & 0xff,
  };
}

function writeConsumerCell(codeL: number, codeH: number): ZhwxMatrixCellWrite {
  return {
    type: ZHWX_CON_TYPE,
    codeL: codeL & 0xff,
    codeH: codeH & 0xff,
  };
}

/** UI 键码 → 协议矩阵单元 */
export function uiKeyCodeToZhwxMatrixCell(
  keyCode: number
): ZhwxMatrixCellWrite | null {
  if (!Number.isFinite(keyCode) || keyCode <= 0) return null;

  if (keyCode === ZHWX_FN_UI_KEYCODE) {
    return writeFuncCell(0x01);
  }

  if (isZhwxFnSyntheticKeyCode(keyCode)) {
    return writeFuncCell(keyCode & 0xff);
  }

  const iconFunc = ZHWX_UI_ICON_TO_FUNC[keyCode];
  if (iconFunc != null) {
    return writeFuncCell(iconFunc);
  }

  if (
    keyCode >= ZHWX_MACRO_UI_BASE &&
    keyCode < ZHWX_MACRO_UI_BASE + ZHWX_MACRO_SLOT_COUNT
  ) {
    const slot = keyCode - ZHWX_MACRO_UI_BASE;
    return buildZhwxMacroMatrixCell(slot);
  }

  const consumerCell = ZHWX_UI_TO_CONSUMER_CELL[keyCode];
  if (consumerCell != null) {
    return writeConsumerCell(consumerCell[0], consumerCell[1]);
  }

  const hotkey = UI_HID_TO_ZHWX_HOTKEY[keyCode];
  if (hotkey != null) {
    return writeKbCell(hotkey, 0);
  }

  if (keyCode <= 0xff) {
    return writeKbCell(0, keyCode);
  }

  return null;
}

/** 写入矩阵 buffer 指定索引 */
export function writeZhwxMatrixCell(
  data: Uint8Array,
  index: number,
  cell: ZhwxMatrixCellWrite
): void {
  const base = index * 3;
  data[base] = cell.type & 0xff;
  data[base + 1] = cell.codeL & 0xff;
  data[base + 2] = cell.codeH & 0xff;
}

/** 清空矩阵格（恢复默认空槽） */
export function clearZhwxMatrixCell(data: Uint8Array, index: number): void {
  writeZhwxMatrixCell(data, index, { type: ZHWX_KB_TYPE, codeL: 0, codeH: 0 });
}
